import React, { useEffect, useRef, useState } from 'react';
import { Typography } from 'antd';
import { WarningOutlined, ArrowRightOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useRole } from '../hooks/useRole';

const { Text } = Typography;

// Task code → route (kept in sync with SetupCenter's taskRouteMap)
const TASK_ROUTES: Record<string, string> = {
  ADMIN_MISSING:                '/v1/users',
  ORG_SETTINGS_INCOMPLETE:      '/v1/organization-management',
  DEPARTMENTS_MISSING:          '/v1/department-management',
  DESIGNATIONS_MISSING:         '/v1/designation-management',
  EMPLOYEES_MISSING:            '/v1/employees',
  SHIFTS_MISSING:               '/v1/shift-management',
  ATTENDANCE_RULES_MISSING:     '/v1/attendance-rules-management',
  EMPLOYEE_SHIFTS_NOT_ASSIGNED: '/v1/shift-management',
  LEAVE_TYPES_MISSING:          '/v1/leaves-types',
  LEAVE_APPROVAL_MISSING:       '/v1/pending-approvals',
  SALARY_TEMPLATE_MISSING:      '/v1/salary-structure-management',
  SALARY_COMPONENTS_MISSING:    '/v1/salary-structure-management',
  EMPLOYEE_SALARY_NOT_ASSIGNED: '/v1/salary-structure-management',
  OVERTIME_RATES_MISSING:       '/v1/overtime-management',
  SETTLEMENT_RULES_MISSING:     '/v1/final-settlement-rules-management',
  NO_JOBS_POSTED:               '/v1/jobs',
  RECRUITMENT_APPROVAL_MISSING: '/v1/pending-approvals',
};

const SetupBanner: React.FC = () => {
  const { setupSummary, setupLoading, refreshSetup } = useAppContext();
  const { hasAdminRole } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const lastRefreshRef = useRef(0);
  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem('setupBannerCollapsed') === '1');

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('setupBannerCollapsed', next ? '1' : '0'); } catch { /* */ }
      return next;
    });
  };

  // Re-evaluate when the user returns to this tab after completing a task elsewhere.
  // Throttled to at most once per 60 seconds to avoid hammering the server.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefreshRef.current > 60_000) {
        lastRefreshRef.current = now;
        refreshSetup();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSetup]);

  // Scoped to the Setup Center screen only — this is the actionable "required tasks"
  // strip that belongs with the setup workspace, not a nag on every page.
  if (!location.pathname.includes('/admin/setup-center')) return null;

  if (setupLoading || !setupSummary || setupSummary.isFullyOperational) return null;
  if (!hasAdminRole) return null;

  // Collect only Required tasks from licensed modules — those are actionable blockers.
  const requiredTasks = setupSummary.modules
    .filter((m) => m.isLicensed)
    .flatMap((m) =>
      (m.pendingTasks ?? [])
        .filter((t) => t.severity === 'Required')
        .map((t) => ({ code: t.code, description: t.description, module: m.displayName })),
    );

  if (requiredTasks.length === 0) return null;

  return (
    <div style={{ padding: '14px 24px 0' }}>
      <style>{`
        .setup-req-chip {
          display: inline-flex; align-items: center; gap: 7px;
          background: #FFFFFF; border: 1px solid #F3D89A; color: #92400E;
          font-size: 12.5px; font-weight: 500; line-height: 1.2;
          padding: 7px 12px; border-radius: 10px; cursor: pointer;
          user-select: none; transition: all .15s ease; text-align: left;
        }
        .setup-req-chip:hover {
          background: #FFF7E6; border-color: #E9B44C;
          box-shadow: 0 2px 8px rgba(217,119,6,0.12); transform: translateY(-1px);
        }
        .setup-req-chip:disabled { cursor: default; }
        .setup-req-chip .arw { transition: transform .15s ease; }
        .setup-req-chip:hover .arw { transform: translateX(2px); }
      `}</style>
      <div
        style={{
          background: 'linear-gradient(135deg, #FFFDF7 0%, #FEF6E2 100%)',
          border: '1px solid #F6E2B0',
          borderRadius: 14,
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          boxShadow: '0 1px 3px rgba(217,119,6,0.05)',
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: 11, flexShrink: 0,
          background: 'linear-gradient(135deg, #FCD34D 0%, #F6B833 100%)', color: '#7C4A03',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          boxShadow: '0 2px 6px rgba(217,119,6,0.25)',
        }}>
          <WarningOutlined />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 14.5, color: '#78350F', lineHeight: 1.2 }}>
              Finish setting up your platform
            </Text>
            <span style={{
              fontSize: 11, fontWeight: 700, color: '#B45309',
              background: '#FDE9B8', borderRadius: 20, padding: '2px 9px', lineHeight: 1.5,
            }}>
              {requiredTasks.length} required
            </span>
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand setup tasks' : 'Collapse setup tasks'}
              style={{
                marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer',
                color: '#B45309', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 4,
              }}
            >
              {collapsed ? 'Show' : 'Hide'}
              {collapsed ? <DownOutlined style={{ fontSize: 10 }} /> : <UpOutlined style={{ fontSize: 10 }} />}
            </button>
          </div>

          {!collapsed && (
            <>
              <Text style={{ fontSize: 12.5, color: '#B45309', display: 'block', marginTop: 3 }}>
                Complete the {requiredTasks.length === 1 ? 'task' : 'tasks'} below to make the platform fully operational.
              </Text>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                {requiredTasks.map((task) => {
                  const route = TASK_ROUTES[task.code];
                  return (
                    <button
                      key={task.code}
                      type="button"
                      className="setup-req-chip"
                      disabled={!route}
                      onClick={route ? () => navigate(route) : undefined}
                    >
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#DC2626', flexShrink: 0 }} />
                      {task.description}
                      {route && <ArrowRightOutlined className="arw" style={{ fontSize: 11, color: '#D97706' }} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupBanner;
